// Redux slice for core application state
import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

// Interface for application configuration
export interface ApplicationConfig {
  region: string;
  productionUnit: string;
  classification: string;
}

// Interface for core slice state
export interface CoreState {
  config: ApplicationConfig; // Stores app config values
  status: 'idle' | 'loading' | 'failed'; // Tracks loading status
  globalError: { message: string; status?: number } | null; // Stores global error info
}

// Initial state for core slice
const initialState: CoreState = {
  config: {
    region: '',
    productionUnit: '',
    classification: '',
  },
  status: 'idle',
  globalError: null,
};

// Core slice definition with reducers for config and error
export const coreSlice = createSlice({
  name: 'core',
  initialState,
  reducers: {
    // Sets the application config
    addConfig: (state, action: PayloadAction<ApplicationConfig>) => {
      state.config = { ...(state.config || {}), ...action.payload };
    },
    updateConfig: (
      state,
      action: PayloadAction<Partial<ApplicationConfig>>
    ) => {
      state.config = { ...state.config, ...action.payload };
    },
    // Sets or clears the global error
    setGlobalError: (
      state,
      action: PayloadAction<{ message: string; status?: number } | null>
    ) => {
      state.globalError = action.payload;
    },
  },
});

// Export actions for use in components
export const { addConfig, updateConfig, setGlobalError } = coreSlice.actions;

// Export reducer for store
export default coreSlice.reducer;
